<template>
    <BaseField
        :label="label"
        :hint="hint"
        :is-optional="isOptional"
        :name="name"
        :error-messages="errorMessagesTotal"
        data-spec-class="field-select"
    >
        <select
            v-bind="$attrs"
            ref="select"
            :name="name"
            :class="classObject"
            :disabled="isDisabled"
            :value="modelValue"
            :data-spec-class="dataSpecClass"
            @change="$emit('update:modelValue', $event.target.value)"
        >
            <option
                v-for="option in options"
                :key="option.key"
                :value="option.key"
                :class="option.class"
            >
                {{ option.label }}
            </option>
        </select>
    </BaseField>
</template>

<script setup lang="ts">
import { withDefaults, defineProps, defineEmits, computed, ref, defineExpose } from 'vue';
import { SelectOption, Theme } from '@/types';
import BaseField from './BaseField.vue';
import { useField } from '@/composables/field';

defineEmits(['update:modelValue']);

const { normalThemeFieldClasses, errorThemeFieldClasses, disabledThemeFieldClasses } = useField();

const normalThemeSelectFieldClasses = normalThemeFieldClasses.replace('form-input', 'form-select');

const select = ref(null);

function focus() {
    select.value.focus();
}

defineExpose({ focus });

interface Props {
    label?: string;
    hint?: string;
    isOptional?: boolean;
    isDisabled?: boolean;
    modelValue?: string;
    vuelidateField?: unknown;
    errorMessages?: string[];
    name: string;
    customThemeBase?: string;
    customThemeNormal?: string;
    dataSpecClass?: string;
    hasAutoDirty?: boolean;
    options?: SelectOption[];
}

const props = withDefaults(defineProps<Props>(), {
    label: '',
    hint: '',
    isOptional: false,
    isDisabled: false,
    modelValue: undefined,
    vuelidateField: undefined,
    errorMessages: () => [],
    customThemeBase: undefined,
    customThemeNormal: undefined,
    dataSpecClass: 'field-select__select',
    hasAutoDirty: true,
    options: () => [],
});

const vuelidateErrors = computed(() => {
    if (props.vuelidateField?.$errors.length) {
        return props.vuelidateField.$errors.map((error) => error.$message);
    }

    return [];
});

const errorMessagesTotal = computed(() => [...vuelidateErrors.value, ...props.errorMessages]);

const theme = computed(() => {
    if (props.isDisabled) {
        return Theme.Disabled;
    }
    if (errorMessagesTotal.value.length) {
        return Theme.Danger;
    }

    return Theme.Normal;
});

const themeBase = computed(() => {
    const defaultThemeBase = 'block w-full rounded-md shadow-xs sm:text-sm';
    return typeof props.customThemeBase === 'string' ? props.customThemeBase : defaultThemeBase;
});

const themeNormal = computed(() => {
    return typeof props.customThemeNormal === 'string'
        ? props.customThemeNormal
        : normalThemeSelectFieldClasses;
});

const classObject = computed(() => ({
    [themeBase.value]: true,
    [themeNormal.value]: theme.value === Theme.Normal,
    [errorThemeFieldClasses]: theme.value === Theme.Danger,
    [disabledThemeFieldClasses]: theme.value === Theme.Disabled,
}));
</script>

<script lang="ts">
export default {
    inheritAttrs: false,
};
</script>
